import { Mesh, SceneLoader, Vector3, type AbstractMesh, type Scene, type TransformNode } from '@babylonjs/core';
import '@babylonjs/loaders/glTF';
import { createLogger, isBreakablePropObject, OBJECT_TYPES, SeededRng, type MapBounds, type Vector2 } from '@sorrem/shared';
import type { MapObjectLayer } from '../map-loader';
import { createBrazierFire, type BrazierFire } from './vfx/brazier-fire';
import { SceneLoadOnceCaches } from './load-once-cache';
import { resolveSceneYaw } from './world-to-scene-position';

const logger = createLogger('engine:map-props');

/** Root URL prop model paths (`propModel`) are relative to. */
const ASSET_ROOT = '/assets/';
/** How far a wall flame sits out from the wall face, in tiles. */
const WALL_FLAME_OFFSET = 0.12;

export interface MapPropBuildResult {
    readonly meshes: readonly AbstractMesh[];
    /** A breakable prop's mesh (#302), by its map object id, to outline it; undefined when this build has none by that id. */
    findBreakablePropMesh(id: string): AbstractMesh | undefined;
    /** The props hung on a wall, with the wall tile each hangs on, so they can go with a cut wall. */
    readonly wallMounted: readonly WallMountedProp[];
    /** Takes away a prop that broke (#302), by its map object id; false when this build has none by that id. */
    removeProp(id: string): boolean;
    /** Animates the props for this frame: their fires' light flickers. */
    update(deltaSeconds: number): void;
    dispose(): void;
}

/** What a breakable prop's instance carries in `metadata`, so a pick knows which map object it hit. */
interface BreakablePropMeshMetadata {
    readonly breakablePropId: string;
}

/** The map object id of the breakable prop a mesh draws; null for any other mesh. */
export function findBreakablePropId(mesh: AbstractMesh): string | null {
    const metadata = mesh.metadata as Partial<BreakablePropMeshMetadata> | null | undefined;
    return typeof metadata?.breakablePropId === 'string' ? metadata.breakablePropId : null;
}

export interface WallMountedProp {
    readonly instance: AbstractMesh;
    readonly wallTile: Vector2;
    /** The prop's flame, if it burns; its light is meant to stay when the prop is hidden. */
    readonly effect?: TransformNode;
}

interface PropPlacement {
    readonly id: string;
    readonly model: string;
    readonly x: number;
    readonly y: number;
    readonly height: number;
    readonly yawDegrees: number;
    readonly elevation: number;
    readonly isOnWall: boolean;
    /** A player's attack breaks it (#302): its instance can be picked, to be clicked. */
    readonly isBreakable: boolean;
    readonly effect?: { readonly kind: string; readonly offsetY: number; readonly width: number; readonly height: number };
}

// One base mesh per model file and scene, shared by every prop instance in
// the scene (and across its map reloads), the same way wall models are cached.
const baseMeshes = new SceneLoadOnceCaches<string, Mesh>((mesh) => mesh.dispose());

/**
 * Draws the props a crucible was decorated with at generation time. Each
 * prop object carries its own model, size and facing (see
 * @sorrem/crucible-generation), so no catalog is needed here. Props are
 * instanced per model and not pickable, so clicks fall through to the floor;
 * only breakable props can be picked, to be clicked and broken.
 * With a `region`, only the props standing on its tiles are built.
 */
export async function buildMapProps(
    scene: Scene,
    objectLayers: readonly MapObjectLayer[],
    tileUnitSize: number,
    region?: MapBounds,
): Promise<MapPropBuildResult> {
    const inRegion = (x: number, y: number): boolean => !region
        || (Math.floor(x) >= region.minX && Math.floor(y) >= region.minY && Math.floor(x) < region.maxX && Math.floor(y) < region.maxY);
    const placements = objectLayers.flatMap((layer) => layer.objects)
        .flatMap((object): PropPlacement[] => {
            const { properties } = object;
            const model = properties['propModel'];
            if (object.type !== OBJECT_TYPES.PROP || typeof model !== 'string' || model === '' || !inRegion(object.x, object.y)) {
                return [];
            }
            const read = (key: string, fallback: number): number => {
                const value = Number(properties[key]);
                return Number.isFinite(value) ? value : fallback;
            };
            const onWall = properties['placement'] === 'on-wall';
            const effectKind = properties['effect'];
            return [{
                id: object.id,
                model,
                x: object.x,
                y: object.y,
                height: read('propHeight', 1),
                yawDegrees: read('rotationDegrees', 0),
                elevation: onWall ? read('wallHeight', 1.2) : 0,
                isOnWall: onWall,
                isBreakable: isBreakablePropObject(object),
                ...(typeof effectKind === 'string' && effectKind !== '' ? {
                    effect: { kind: effectKind, offsetY: read('effectOffsetY', 0), width: read('effectWidth', 0.3), height: read('effectHeight', 0.4) },
                } : {}),
            }];
        });

    const instances: AbstractMesh[] = [];
    const wallMounted: WallMountedProp[] = [];
    // Each prop's own pieces, by map object id, so a broken one goes whole.
    const placed = new Map<string, { readonly instance: AbstractMesh; readonly fire: BrazierFire | null; readonly wallMounted: WallMountedProp | null }>();
    const models = [...new Set(placements.map((placement) => placement.model))];
    const bases = await Promise.all(models.map(async (model) => {
        try {
            return [model, await loadBaseMesh(scene, model)] as const;
        } catch (error) {
            logger.warn(`Failed to load prop model ${model}:`, error);
            return [model, null] as const;
        }
    }));
    const baseByModel = new Map(bases);

    for (const placement of placements) {
        const base = baseByModel.get(placement.model);
        if (!base) {
            continue;
        }
        // A breakable prop is its own mesh (sharing the model's geometry), not an instance, so it can be
        // outlined alone when hovered, as a chest is; the other props are instances, drawn in one batch.
        const instance = placement.isBreakable ? cloneBreakableProp(base, placement.id) : base.createInstance(`prop-${placement.id}`);
        const modelHeight = base.getBoundingInfo().boundingBox.extendSize.y * 2;
        instance.scaling.setAll(modelHeight > 0 ? placement.height / modelHeight : 1);
        // Tile x maps to scene z and tile y to scene x (see worldSpaceToScenePosition).
        instance.position = new Vector3(placement.y * tileUnitSize, placement.elevation, placement.x * tileUnitSize);
        instance.rotationQuaternion = null;
        instance.rotation.y = resolveSceneYaw(placement.yawDegrees);
        instance.isPickable = placement.isBreakable;
        if (placement.isBreakable) {
            instance.metadata = { breakablePropId: placement.id } satisfies BreakablePropMeshMetadata;
        }
        instances.push(instance);

        const fire = placement.effect?.kind === 'fire' ? createFlame(scene, placement, tileUnitSize) : null;
        const mounted = placement.isOnWall ? { instance, wallTile: resolveWallTileBehind(placement), ...(fire ? { effect: fire.root } : {}) } : null;
        if (mounted) {
            wallMounted.push(mounted);
        }
        placed.set(placement.id, { instance, fire, wallMounted: mounted });
    }

    return {
        meshes: instances,
        wallMounted,
        findBreakablePropMesh: (id: string): AbstractMesh | undefined => {
            const prop = placed.get(id);
            return prop && findBreakablePropId(prop.instance) !== null ? prop.instance : undefined;
        },
        removeProp: (id: string): boolean => {
            const prop = placed.get(id);
            if (!prop) {
                return false;
            }
            placed.delete(id);
            instances.splice(instances.indexOf(prop.instance), 1);
            if (prop.wallMounted) {
                wallMounted.splice(wallMounted.indexOf(prop.wallMounted), 1);
            }
            prop.instance.dispose();
            prop.fire?.dispose();
            return true;
        },
        update: (deltaSeconds: number): void => {
            placed.forEach(({ fire }) => fire?.update(deltaSeconds));
        },
        dispose: (): void => {
            placed.forEach(({ instance, fire }) => {
                instance.dispose();
                fire?.dispose();
            });
            placed.clear();
        },
    };
}

/** The wall tile a wall prop hangs on: half a tile behind it, against the way it faces. */
function resolveWallTileBehind(placement: PropPlacement): Vector2 {
    const radians = (placement.yawDegrees * Math.PI) / 180;
    return {
        x: Math.floor(placement.x - Math.sin(radians) * 0.5),
        y: Math.floor(placement.y + Math.cos(radians) * 0.5),
    };
}

function createFlame(scene: Scene, placement: PropPlacement, tileUnitSize: number): BrazierFire {
    const effect = placement.effect!;
    const radians = (placement.yawDegrees * Math.PI) / 180;
    // A wall torch's flame sits a little out from the wall, the way the torch leans.
    const outward = placement.isOnWall ? WALL_FLAME_OFFSET : 0;
    const tileX = placement.x + Math.sin(radians) * outward;
    const tileY = placement.y - Math.cos(radians) * outward;
    const rng = new SeededRng(placement.id);
    return createBrazierFire(scene, {
        position: new Vector3(tileY * tileUnitSize, placement.elevation + effect.offsetY, tileX * tileUnitSize),
        width: effect.width,
        height: effect.height,
        flameSpeed: 2,
        flameBrightness: 0.55,
        // Every torch casting light would blow past Babylon's per-material light limit, so props glow without lighting.
        lightIntensity: 0,
        lightRange: 0,
        timeOffsetSeconds: rng.random() * 10,
        flickerPhase: rng.random() * Math.PI * 2,
    });
}

/**
 * Loads prop models ahead of the map chunks that place them, so a chunk
 * built later loads nothing. A model that fails to load is skipped (and tried
 * again when a chunk needs it), as when building.
 */
export async function preloadPropModels(scene: Scene, models: readonly string[]): Promise<void> {
    await Promise.all(models.map((model) => loadBaseMesh(scene, model).catch((error: unknown) => {
        logger.warn(`Failed to preload prop model ${model}:`, error);
    })));
}

/** A breakable prop's own copy of its hidden base mesh: the same geometry and material, shown. */
function cloneBreakableProp(base: Mesh, id: string): Mesh {
    const clone = base.clone(`prop-${id}`, null, true);
    clone.isVisible = true;
    return clone;
}

function loadBaseMesh(scene: Scene, model: string): Promise<Mesh> {
    return baseMeshes.of(scene).get(model, () => importBaseMesh(scene, model));
}

/** Imports a prop model as one hidden base mesh: merged, transform baked, centered with its base on the floor. */
async function importBaseMesh(scene: Scene, model: string): Promise<Mesh> {
    const path = `${ASSET_ROOT}${model}`;
    const slash = path.lastIndexOf('/');
    const result = await SceneLoader.ImportMeshAsync('', path.slice(0, slash + 1), path.slice(slash + 1), scene);
    const geometry = result.meshes.filter((mesh): mesh is Mesh => mesh instanceof Mesh && mesh.getTotalVertices() > 0);
    if (geometry.length === 0) {
        throw new Error(`No geometry in ${model}`);
    }
    let merged: Mesh | null;
    if (geometry.length === 1) {
        merged = geometry[0]!;
        merged.computeWorldMatrix(true);
        merged.bakeTransformIntoVertices(merged.getWorldMatrix());
        merged.parent = null;
    } else {
        // Merging bakes each source mesh's world transform (the glTF root's handedness flip included).
        merged = Mesh.MergeMeshes(geometry, true, true, undefined, true, true);
    }
    if (!merged) {
        throw new Error(`Could not merge ${model}`);
    }
    for (const leftover of result.meshes) {
        if (leftover !== merged && !leftover.isDisposed()) {
            leftover.dispose();
        }
    }

    merged.position.setAll(0);
    merged.rotationQuaternion = null;
    merged.rotation.setAll(0);
    merged.scaling.setAll(1);
    merged.refreshBoundingInfo();
    const box = merged.getBoundingInfo().boundingBox;
    const center = box.minimum.add(box.maximum).scale(0.5);
    merged.position = new Vector3(-center.x, -box.minimum.y, -center.z);
    merged.bakeCurrentTransformIntoVertices();
    merged.refreshBoundingInfo();
    merged.isVisible = false;
    merged.isPickable = false;
    merged.name = `prop-base-${model}`;
    return merged;
}
